import { createStore } from 'vuex'
import api from '../services/api'
import UserService from '../services/user-service'
import PermissionsService from '../services/permissions-service'

const ID_TOKEN_KEY = 'lamusica_platform_user_token'

const auth = {
  namespaced: true,

  state: () => ({
    user: UserService.getUser(),
    token: localStorage.getItem(ID_TOKEN_KEY) || null,
    permissions: PermissionsService.getPermissions()
  }),

  getters: {
    isAuthenticated: state => !!state.token,
    user: state => state.user,
    token: state => state.token,
    permissions: state => state.permissions,
    isAdmin: () => UserService.isAdmin(),
    hasPermission: () => entry => UserService.hasPermission(entry)
  },

  mutations: {
    SET_USER(state, user) {
      state.user = user
      if (user) {
        UserService.saveUser(user)
      } else {
        UserService.destroyUser()
      }
    },
    SET_TOKEN(state, token) {
      state.token = token
      if (token) {
        localStorage.setItem(ID_TOKEN_KEY, token)
      } else {
        localStorage.removeItem(ID_TOKEN_KEY)
      }
    },
    SET_PERMISSIONS(state, permissions) {
      state.permissions = permissions || []
      if (permissions?.length) {
        PermissionsService.savePermissions(permissions)
      } else {
        PermissionsService.destroyPermissions()
      }
    }
  },

  actions: {
    async login({ commit }, { email, password }) {
      const response = await api.post('/auth/login', { email, password })
      commit('SET_TOKEN', response.data.token)
      commit('SET_USER', response.data.user)
      commit('SET_PERMISSIONS', response.data.permissions)
      return response.data
    },

    async fetchUser({ commit }) {
      const response = await api.get('/auth/me')
      commit('SET_USER', response.data.user)
      commit('SET_PERMISSIONS', response.data.permissions)
      return response.data
    },

    logout({ commit }) {
      commit('SET_TOKEN', null)
      commit('SET_USER', null)
      commit('SET_PERMISSIONS', [])
    }
  }
}

const loading = {
  namespaced: true,

  state: () => ({
    activeRequests: 0
  }),

  getters: {
    isLoading: state => state.activeRequests > 0
  },

  mutations: {
    INCREMENT(state) { state.activeRequests++ },
    DECREMENT(state) { if (state.activeRequests > 0) state.activeRequests-- }
  },

  actions: {
    start({ commit }) { commit('INCREMENT') },
    stop({ commit }) { commit('DECREMENT') }
  }
}

const connection = {
  namespaced: true,

  state: () => ({
    online: true
  }),

  getters: {
    isOnline: state => state.online
  },

  mutations: {
    SET_ONLINE(state, value) {
      state.online = value
    }
  },

  actions: {
    setOffline({ commit }) {
      commit('SET_ONLINE', false)
    },
    setOnline({ commit }) {
      commit('SET_ONLINE', true)
    }
  }
}

let toastId = 0

const toast = {
  namespaced: true,

  state: () => ({
    items: []
  }),

  getters: {
    items: state => state.items
  },

  mutations: {
    ADD(state, item) {
      state.items.push(item)
    },
    REMOVE(state, id) {
      state.items = state.items.filter(i => i.id !== id)
    }
  },

  actions: {
    show({ commit }, { message, title, type = 'error', duration = 5000 }) {
      const id = ++toastId
      commit('ADD', { id, message, title, type })
      if (duration > 0) {
        setTimeout(() => commit('REMOVE', id), duration)
      }
    },
    dismiss({ commit }, id) {
      commit('REMOVE', id)
    },
    success({ dispatch }, message) {
      dispatch('show', { message, title: 'Success', type: 'success' })
    },
    error({ dispatch }, message) {
      dispatch('show', { message, title: 'Error', type: 'error' })
    },
    warning({ dispatch }, message) {
      dispatch('show', { message, title: 'Warning', type: 'warning' })
    }
  }
}

import editor from './editor'
import analytics from './analytics'

export default createStore({
  modules: { auth, connection, loading, toast, editor, analytics }
})
